// src/modules/competition/components/TeamMembersModal/index.js
import React, { useState, useEffect } from 'react';
import { Modal, Table, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useService } from '../../../../shared/hooks/useService';
import { DEFAULT_TEAM_ICON } from '../TeamManager';

const SortIcon = ({ field, sortBy, sortDir }) => {
    if (sortBy !== field) return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.75rem' }} />;
    return sortDir === 'asc'
        ? <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.75rem' }} />
        : <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.75rem' }} />;
};

const TeamMembersModal = ({ show, onHide, competitionId, competitors, teams: teamsProp, onTeamsUpdate }) => {
    const [teams, setTeams] = useState(teamsProp);
    const [saving, setSaving] = useState({});   // { username: true }
    const [errors, setErrors] = useState({});   // { username: 'message' }
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const service = useService();

    // Re-sync when the modal opens with fresh data
    useEffect(() => {
        if (show) setTeams(teamsProp);
    }, [show, teamsProp]);

    const getTeamIdForUser = (username) => {
        const team = teams.find(t => (t.members || []).includes(username));
        return team ? team.teamId : '';
    };

    const handleReassign = async (username, teamId) => {
        setSaving(prev => ({ ...prev, [username]: true }));
        setErrors(prev => ({ ...prev, [username]: null }));

        const updatedTeams = teams.map(t => {
            const members = (t.members || []).filter(m => m !== username);
            if (t.teamId === teamId) return { ...t, members: [...members, username] };
            return { ...t, members };
        });

        // Optimistic update
        setTeams(updatedTeams);

        try {
            await service.reassignTeamMember(competitionId, username, teamId || null);
            onTeamsUpdate(updatedTeams);
        } catch (err) {
            // Roll back
            setTeams(teams);
            setErrors(prev => ({ ...prev, [username]: err.message || 'Failed to reassign' }));
        } finally {
            setSaving(prev => ({ ...prev, [username]: false }));
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir('asc');
        }
    };

    const getTeamName = (username) => {
        const team = teams.find(t => (t.members || []).includes(username));
        return team ? team.name.toLowerCase() : '￿'; // unassigned sorts last
    };

    const sortedCompetitors = [...competitors].sort((a, b) => {
        let cmp = 0;
        if (sortBy === 'name') {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
            cmp = nameA.localeCompare(nameB);
        } else {
            cmp = getTeamName(a.username).localeCompare(getTeamName(b.username));
        }
        return sortDir === 'asc' ? cmp : -cmp;
    });

    const teamOptions = [
        { value: '', label: 'Unassigned' },
        ...teams.map(t => ({ value: t.teamId, label: `${t.icon || DEFAULT_TEAM_ICON} ${t.name}` })),
    ];

    const hasErrors = Object.values(errors).some(Boolean);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-people me-2"></i>
                    Manage Team Members
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0">
                {hasErrors && (
                    <Alert variant="danger" className="m-3 mb-0">
                        Some reassignments failed — check individual rows below.
                    </Alert>
                )}

                <Table hover responsive className="mb-0">
                    <thead className="sticky-top" style={{ background: 'inherit' }}>
                        <tr>
                            <th
                                className="ps-3"
                                onClick={() => handleSort('name')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Competitor
                                <SortIcon field="name" sortBy={sortBy} sortDir={sortDir} />
                            </th>
                            <th
                                style={{ width: '220px', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('team')}
                            >
                                Team
                                <SortIcon field="team" sortBy={sortBy} sortDir={sortDir} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedCompetitors.map(competitor => {
                            const currentTeamId = getTeamIdForUser(competitor.username);
                            const isSaving = Boolean(saving[competitor.username]);
                            const rowError = errors[competitor.username];

                            return (
                                <tr key={competitor.username} className={rowError ? 'table-danger' : undefined}>
                                    <td className="ps-3 align-middle">
                                        <div className="d-flex align-items-center gap-2">
                                            {competitor.profilePicture ? (
                                                <img
                                                    src={competitor.profilePicture}
                                                    alt=""
                                                    width={28}
                                                    height={28}
                                                    style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: '50%',
                                                    background: 'var(--bs-secondary-bg)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.7rem', flexShrink: 0,
                                                }}>
                                                    {(competitor.firstName?.[0] || '?').toUpperCase()}
                                                </div>
                                            )}
                                            <span>
                                                {competitor.firstName} {competitor.lastName}
                                                {!currentTeamId && (
                                                    <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                                        Unassigned
                                                    </Badge>
                                                )}
                                            </span>
                                        </div>
                                        {rowError && (
                                            <div className="text-danger small mt-1">{rowError}</div>
                                        )}
                                    </td>
                                    <td className="align-middle pe-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Select
                                                size="sm"
                                                value={currentTeamId}
                                                onChange={e => handleReassign(competitor.username, e.target.value)}
                                                disabled={isSaving}
                                                style={{ flexGrow: 1 }}
                                            >
                                                {teamOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {isSaving && (
                                                <Spinner size="sm" animation="border" style={{ flexShrink: 0 }} />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Modal.Body>
        </Modal>
    );
};

TeamMembersModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    competitionId: PropTypes.string.isRequired,
    competitors: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        profilePicture: PropTypes.string,
    })).isRequired,
    teams: PropTypes.arrayOf(PropTypes.shape({
        teamId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        icon: PropTypes.string,
        members: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
    onTeamsUpdate: PropTypes.func.isRequired,
};

export default TeamMembersModal;
